/**
 * Vehicle Configuration Page
 * Manage vehicle types, country rules, filter visibility
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  ArrowLeft, Car, Globe, Filter, Sparkles, Save,
  Settings, ChevronDown, ChevronRight
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../components/ui/accordion';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleConfigPage = () => {
  const [activeTab, setActiveTab] = useState('vehicle-types');
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, countriesRes, featuresRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/vehicles/config/vehicle-types`),
        axios.get(`${API_URL}/api/admin/vehicles/config/countries`),
        axios.get(`${API_URL}/api/admin/vehicles/features`)
      ]);
      setVehicleTypes(typesRes.data.vehicle_types || []);
      setCountries(countriesRes.data.countries || []);
      setFeatures(featuresRes.data.toggles || []);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleVehicleType = async (slug, field, value) => {
    const updated = vehicleTypes.map(vt => 
      vt.slug === slug ? { ...vt, [field]: value } : vt
    );
    setVehicleTypes(updated);
  };

  const toggleFeature = async (featureKey, isEnabled) => {
    try {
      await axios.post(`${API_URL}/api/admin/vehicles/features?admin_id=admin`, {
        feature_key: featureKey,
        is_enabled: isEnabled,
        scope: 'global'
      });
      setFeatures(features.map(f => 
        f.feature_key === featureKey ? { ...f, is_enabled: isEnabled } : f
      ));
    } catch (error) {
      console.error('Failed to toggle feature:', error);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      // Save vehicle type changes
      for (const vt of vehicleTypes) {
        await axios.post(`${API_URL}/api/admin/vehicles/config/vehicle-types?admin_id=admin`, vt);
      }
      alert('Changes saved successfully!');
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6 text-blue-600" />
              Configuration
            </h1>
            <p className="text-gray-500">Vehicle types, country rules, feature toggles</p>
          </div>
        </div>
        <Button onClick={saveChanges} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="vehicle-types">
            <Car className="h-4 w-4 mr-2" />
            Vehicle Types
          </TabsTrigger>
          <TabsTrigger value="countries">
            <Globe className="h-4 w-4 mr-2" />
            Countries
          </TabsTrigger>
          <TabsTrigger value="filters">
            <Filter className="h-4 w-4 mr-2" />
            Filter Rules
          </TabsTrigger>
          <TabsTrigger value="features">
            <Sparkles className="h-4 w-4 mr-2" />
            Features
          </TabsTrigger>
        </TabsList>

        {/* Vehicle Types Tab */}
        <TabsContent value="vehicle-types" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Vehicle Types per Country</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Vehicle Type</th>
                      <th className="text-center py-3 px-4">🇱🇰 Sri Lanka</th>
                      <th className="text-center py-3 px-4">🇬🇧 UK</th>
                      <th className="text-center py-3 px-4">🇪🇺 EU</th>
                      <th className="text-center py-3 px-4">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleTypes.map((vt) => (
                      <tr key={vt.slug} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{vt.icon}</span>
                            <span className="font-medium">{vt.name}</span>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch
                            checked={vt.enabled_countries?.includes('SL')}
                            onCheckedChange={(checked) => {
                              const countries = checked 
                                ? [...(vt.enabled_countries || []), 'SL']
                                : (vt.enabled_countries || []).filter(c => c !== 'SL');
                              toggleVehicleType(vt.slug, 'enabled_countries', countries);
                            }}
                          />
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch
                            checked={vt.enabled_countries?.includes('UK')}
                            onCheckedChange={(checked) => {
                              const countries = checked 
                                ? [...(vt.enabled_countries || []), 'UK']
                                : (vt.enabled_countries || []).filter(c => c !== 'UK');
                              toggleVehicleType(vt.slug, 'enabled_countries', countries);
                            }}
                          />
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch
                            checked={vt.enabled_countries?.includes('EU')}
                            onCheckedChange={(checked) => {
                              const countries = checked 
                                ? [...(vt.enabled_countries || []), 'EU']
                                : (vt.enabled_countries || []).filter(c => c !== 'EU');
                              toggleVehicleType(vt.slug, 'enabled_countries', countries);
                            }}
                          />
                        </td>
                        <td className="text-center py-3 px-4">
                          <Switch
                            checked={vt.is_active}
                            onCheckedChange={(checked) => toggleVehicleType(vt.slug, 'is_active', checked)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Countries Tab */}
        <TabsContent value="countries" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {countries.map((country) => (
              <Card key={country.country_code}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">
                      {country.country_code === 'SL' ? '🇱🇰' : 
                       country.country_code === 'UK' ? '🇬🇧' : '🇪🇺'}
                    </span>
                    {country.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Currency</p>
                    <p className="font-medium">{country.currency_symbol} {country.currency}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Units</p>
                    <p className="font-medium">{country.distance_unit} / {country.mileage_unit}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Enabled Fields</p>
                    <div className="flex flex-wrap gap-1">
                      {country.enabled_fields?.map((field) => (
                        <Badge key={field} variant="outline" className="text-xs bg-green-50">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Disabled Fields</p>
                    <div className="flex flex-wrap gap-1">
                      {country.disabled_fields?.map((field) => (
                        <Badge key={field} variant="outline" className="text-xs bg-red-50">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Filter Rules Tab */}
        <TabsContent value="filters" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Filter Field Visibility Rules</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" defaultValue={['primary', 'smart']}>
                <AccordionItem value="primary">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">PRIMARY</Badge>
                      Always visible, highest priority
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                      {['make', 'model', 'priceRange', 'year', 'mileage', 'fuelType', 'location', 'distance'].map(field => (
                        <div key={field} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{field}</span>
                          <Switch defaultChecked />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="smart">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700">SMART</Badge>
                      AI-powered, trust & cost filters
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                      {['runningCostBand', 'insuranceEstimateBand', 'verifiedSeller', 'inspectionAvailable', 'notWrittenOff', 'priceRating'].map(field => (
                        <div key={field} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{field}</span>
                          <Switch defaultChecked />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="advanced">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-700">ADVANCED</Badge>
                      Detailed specifications
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                      {['transmission', 'bodyType', 'driveType', 'engineSize', 'doors', 'seats', 'colour'].map(field => (
                        <div key={field} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{field}</span>
                          <Switch defaultChecked />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="auto">
                  <AccordionTrigger>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-gray-100 text-gray-700">AUTO</Badge>
                      Auto-calculated fields
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                      {['co2Emissions', 'taxEstimate', 'complianceStatus'].map(field => (
                        <div key={field} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span className="text-sm">{field}</span>
                          <Switch defaultChecked />
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Feature Toggles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {features.map((feature) => (
                  <div key={feature.feature_key} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium capitalize">
                        {feature.feature_key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-sm text-gray-500">Scope: {feature.scope}</p>
                    </div>
                    <Switch
                      checked={feature.is_enabled}
                      onCheckedChange={(checked) => toggleFeature(feature.feature_key, checked)}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VehicleConfigPage;
